/* DeviceDetailsViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./DeviceDetailsViewRenderer");

const PLATFORM_FEED_LIMIT = 10000; // using a considerably large number to get all platforms

// Mirrors the Settings > Custom Fields > Tags validation (see CustomFieldsComponent).
const MAX_TAG_NAME_LENGTH = 32;
const TAG_NAME_CHARSET_PATTERN = /^[a-zA-Z0-9 .\-_&@:()[\]]*$/;
const TAG_NAME_LENGTH_ERROR_MSG = (name) => `Tag name "${name}" must be 32 characters or fewer.`;
const TAG_NAME_CHARSET_ERROR_MSG = (name) =>
    `The Tag name "${name}" can only contain letters, numbers, spaces, ".", "-", "_", "&", "@", ":", "(", ")", "[", "]"`;

class DeviceDetailsViewComponent extends EventEmitter {
    constructor(
        logger,
        gameEditor,
        userSession,
        CustomFieldsEntity,
        OsImageServerEntity,
        EngineZoneEntity,
        OrgZoneEntity,
        LicenseRestriction,
        MUF,
        solutionsManager,
        storageFactory,
        DeviceEntity,
        SiteEntity,
        ConnectionInfo,
        jnibAdapter,
        Credentials,
        ExtendedPropType,
        ExtendedProps,
        OdmOsPlatformEntity,
        ValueMap,
        Tags
    ) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = MUF;

        this._userSession = userSession;
        this._CustomFieldsEntityDef = CustomFieldsEntity;
        this._osImageServerEntity = OsImageServerEntity;
        this._siteEntity = SiteEntity;
        this._connectionInfoEntityDef = ConnectionInfo;
        this._engineZoneEntity = EngineZoneEntity;
        this._customFieldEntity = undefined;
        this._orgZoneEntity = OrgZoneEntity;
        this._TagsEntity = Tags;
        this._licenseRestriction = LicenseRestriction;
        this._storageFactory = storageFactory;
        this._nibApiService = jnibAdapter.getNibApiService();
        this._isOSMSolutionAvailable = this._licenseRestriction.checkLicense("OSM");
        this._isTopologyAvailable = this._licenseRestriction.checkLicense("TOP");
        const authorizedActions = _.get(userSession, "_userEntity.authorizedActions", []);
        this._canCreateTagsOnTheFly =
            authorizedActions.indexOf("customFieldsWrite") > -1 &&
            authorizedActions.indexOf("otfTagCreate") > -1;
        this._osImageServerFeed = undefined;
        this._siteFeed = undefined;
        this._sitesList = undefined;
        this._engineZoneFeed = undefined;
        this._connectionDropdownList = undefined;
        this._connectionTypeDefs = undefined;
        this._isDeviceControlled = false;
        this._CredentialsEntity = Credentials;
        this._extendedPropTypeEntityDef = ExtendedPropType;
        this._extendedPropTypes = [];
        this._extendedPropsEntityDef = ExtendedProps;
        this._extendedPropFeed = {};
        this._deviceCategory = "";
        this._isEdpEntityAddedOrRemoved = false;
        this._ValueMap = ValueMap;
        this._manufacturerOptions = [];
        this._modelOptions = [];
        this._deviceTypeOptions = [];
        // TODO Quick hack to customize DeviceDetailsView based on its parent solution (Device Manager or Config Drift)
        // To be refactored with separate views once form layout / style specifications have matured
        this._solutionDefinition = solutionsManager.getActiveSolution();
        this._solutionName = this._solutionDefinition.name || "";
        this._device = undefined;
        this._customFieldColumnDef = undefined;
        this._haPairFeed = undefined;
        this._restoreCommandsWithCredsName = undefined;
        this._odmOsPlatformEntityDef = OdmOsPlatformEntity;
        this._odmOsPlatformsFeed = undefined;
        this._odmValueMapFeed = undefined;
        this._tags = undefined;
        this._tagOptions = undefined;

        this._extendedReadOnlyProps = [];

        // Debouncing this event to run once in 5 seconds so that the device editor is not
        // swamped with Site entity updates
        this._onSiteFeedUpdated = _.debounce(this._onSiteFeedUpdated.bind(this), 5000);
        // Bind and debounce extended prop listeners once in constructor
        this._onEdpEntityRemoved = _.debounce(this._onEdpEntityRemoved.bind(this), 500);
        this._onEdpEntityAdded = _.debounce(this._onEdpEntityAdded.bind(this), 500);
        // Bind other listeners once in constructor
        this._onDirtyStateChanged = this._onDirtyStateChanged.bind(this);
        this._onEdpPristinePropertiesChanged = this._onEdpPristinePropertiesChanged.bind(this);
        this._onCustomFieldsUpdated = this._onCustomFieldsUpdated.bind(this);
        this._onOsImageServerFeedUpdated = this._onOsImageServerFeedUpdated.bind(this);
        this._onPendingPatchesChanged = this._onPendingPatchesChanged.bind(this);
        this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this);
        this._onInstanceRemovedFromFeed = this._onInstanceRemovedFromFeed.bind(this);
        this._onEngineZoneFeedUpdated = this._onEngineZoneFeedUpdated.bind(this);
        this._onOrgZoneFeedUpdated = this._onOrgZoneFeedUpdated.bind(this);
        this._onOdmValueMapFeedUpdated = this._onOdmValueMapFeedUpdated.bind(this);
        this._onOdmOsPlatformEntityListChanged = _.debounce(
            this._onOdmOsPlatformEntityListChanged.bind(this),
            500
        );
        this._renderer = new Renderer(this);
        this._DeviceEntity = DeviceEntity;
        this._registerListeners();
    }

    get customFieldsJtypes() {
        return Object.entries(this._customFieldColumnDef)
            .filter(
                ([id, customFieldDef]) =>
                    _.get(customFieldDef, "enabled") &&
                    (!_.get(customFieldDef, "systemOwned") ||
                        (_.get(
                            this._customFieldEntity,
                            `_pristineProperties.fields.${id}.source`,
                            ""
                        ) === "ControllerDiscoveryConfig" &&
                            ((_.toLower(_.get(this._customFieldColumnDef, `${id}.os`, "")) ===
                                _.toLower(
                                    _.get(this._device, "_pristineProperties.detectedOs", "")
                                ) &&
                                _.toLower(_.get(this._customFieldColumnDef, `${id}.vendor`, "")) ===
                                    _.toLower(
                                        _.get(
                                            this._device,
                                            "_pristineProperties.detectedVendor",
                                            ""
                                        )
                                    )) ||
                                (_.isEmpty(_.get(this._customFieldColumnDef, `${id}.os`)) &&
                                    _.isEmpty(_.get(this._customFieldColumnDef, `${id}.vendor`))) ||
                                !_.isNil(
                                    _.get(
                                        this._device,
                                        `_pristineProperties.customFieldValues.${id}`
                                    )
                                ))))
            )
            .map(([customFieldId, customFieldDef]) =>
                Object.assign(
                    {id: customFieldId},
                    {
                        source: _.get(
                            this._customFieldEntity,
                            `_pristineProperties.fields.${customFieldId}.source`,
                            ""
                        ),
                    },
                    customFieldDef
                )
            )
            .sort(this._CustomFieldsEntityDef.getSortingComparator("order"))
            .map(this.gameEditor.generateCustomFieldJtype);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerDeviceListeners() {
        this._device.on("DirtyStateChanged", this._onDirtyStateChanged);
        this._device.on("PendingPatchesChanged", this._onPendingPatchesChanged);
        this._device.on("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    _unregisterDeviceListeners() {
        this._device.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
        this._device.removeListener("PendingPatchesChanged", this._onPendingPatchesChanged);
        this._device.removeListener("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    _registerExtendedPropsListeners() {
        this._unregisterExtendedPropsListeners();
        this._extendedPropFeed.on("EntityRemoved", this._onEdpEntityRemoved);
        this._extendedPropFeed.on("EntityAdded", this._onEdpEntityAdded);
        _.values(this._extendedPropFeed.getCache()).forEach((extendedProp) => {
            extendedProp.on("DirtyStateChanged", this._onDirtyStateChanged);
            extendedProp.on("PristinePropertiesChanged", this._onEdpPristinePropertiesChanged);
        });
    }

    _unregisterExtendedPropsListeners() {
        this._extendedPropFeed.removeAllListeners("EntityRemoved");
        this._extendedPropFeed.removeAllListeners("EntityAdded");
        _.values(this._extendedPropFeed.getCache()).forEach((extendedProp) => {
            extendedProp.removeListener("DirtyStateChanged", this._onDirtyStateChanged);
            extendedProp.removeListener(
                "PristinePropertiesChanged",
                this._onEdpPristinePropertiesChanged
            );
        });
    }

    _registerCustomFieldListeners() {
        this._customFieldEntity.on("PropertiesUpdated", this._onCustomFieldsUpdated);
    }

    _unregisterCustomFieldListeners() {
        this._customFieldEntity.removeListener("PropertiesUpdated", this._onCustomFieldsUpdated);
    }

    _registerServerFeedListeners() {
        if (!_.isEmpty(this._osImageServerFeed)) {
            this._osImageServerFeed.on("EntityPropertiesUpdated", this._onOsImageServerFeedUpdated);
        }
    }

    _unregisterServerFeedListeners() {
        this._osImageServerFeed.removeListener(
            "EntityPropertiesUpdated",
            this._onOsImageServerFeedUpdated
        );
    }

    _registerSiteFeedListeners() {
        // this._siteFeed.on(
        //     "EntityPropertiesUpdated",
        //     (this._onSiteFeedUpdated = this._onSiteFeedUpdated.bind(this))
        // );
        // this._siteFeed.on("NewpageEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.on("AddEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.on("UpdateEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.on("InstanceRemoved", this._onInstanceRemovedFromFeed);
    }

    _unregisterSiteFeedListeners() {
        // this._siteFeed.removeListener(
        //     "EntityPropertiesUpdated",
        //     this._onSiteFeedUpdated
        // );
        // this._siteFeed.removeListener("NewpageEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.removeListener("AddEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.removeListener("UpdateEventReceived", this._onSiteFeedUpdated);
        this._siteFeed.removeListener("InstanceRemoved", this._onInstanceRemovedFromFeed);
    }

    _registerEngineZoneListeners() {
        this._engineZoneFeed.on("EntityPropertiesUpdated", this._onEngineZoneFeedUpdated);
    }

    _unregisterEngineZoneFeedListeners() {
        this._engineZoneFeed.removeListener(
            "EntityPropertiesUpdated",
            this._onEngineZoneFeedUpdated
        );
    }

    _registerOrgZoneListeners() {
        this._orgZoneFeed.on("EntityPropertiesUpdated", this._onOrgZoneFeedUpdated);
    }

    _unregisterOrgZoneFeedListeners() {
        this._orgZoneFeed.removeListener("EntityPropertiesUpdated", this._onOrgZoneFeedUpdated);
    }

    _registerOdmOsPlatformFeedListeners() {
        if (!_.isEmpty(this._odmOsPlatformsFeed)) {
            this._odmOsPlatformsFeed.on("EntityAdded", this._onOdmOsPlatformEntityListChanged);
            this._odmOsPlatformsFeed.on("EntityRemoved", this._onOdmOsPlatformEntityListChanged);
        }
    }

    _unregisterOdmOsPlatformFeedListeners() {
        if (!_.isEmpty(this._odmOsPlatformsFeed)) {
            this._odmOsPlatformsFeed.removeListener(
                "EntityAdded",
                this._onOdmOsPlatformEntityListChanged
            );
            this._odmOsPlatformsFeed.removeListener(
                "EntityRemoved",
                this._onOdmOsPlatformEntityListChanged
            );
        }
    }

    _onOdmOsPlatformEntityListChanged() {
        this.emit("PlatformOptionsUpdated");
    }

    _registerOdmValueMapFeedListeners() {
        if (!_.isEmpty(this._odmValueMapFeed)) {
            this._odmValueMapFeed.on("EntityPropertiesUpdated", this._onOdmValueMapFeedUpdated);
        }
    }

    _unregisterOdmValueMapFeedListeners() {
        if (!_.isEmpty(this._odmValueMapFeed)) {
            this._odmValueMapFeed.removeListener(
                "EntityPropertiesUpdated",
                this._onOdmValueMapFeedUpdated
            );
        }
    }

    _onOdmValueMapFeedUpdated() {
        this._setOdmFieldOptions();
        this.emit("OdmValueMapFeedUpdated");
    }

    _unregisterAllListeners() {
        this._unregisterListeners();
        this._unregisterDeviceListeners();
        this._unregisterExtendedPropsListeners();
        this._unregisterCustomFieldListeners();
        this._unregisterServerFeedListeners();
        this._unregisterSiteFeedListeners();
        this._unregisterEngineZoneFeedListeners();
        this._unregisterOrgZoneFeedListeners();
        this._unregisterOdmOsPlatformFeedListeners();
        this._unregisterOdmValueMapFeedListeners();
    }
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    async shutdown() {
        // Cancel debounced handlers so none fire after teardown against nulled members
        this._onSiteFeedUpdated.cancel();
        this._onEdpEntityRemoved.cancel();
        this._onEdpEntityAdded.cancel();
        this._onOdmOsPlatformEntityListChanged.cancel();

        if (!_.isEmpty(this._device)) {
            this._unregisterDeviceListeners();
        }
        if (!_.isEmpty(this._extendedPropFeed)) {
            this._unregisterExtendedPropsListeners();
            await this._extendedPropFeed.shutdown();
        }
        if (!_.isEmpty(this._osImageServerFeed)) {
            this._unregisterServerFeedListeners();
            await this._osImageServerFeed.shutdown();
        }
        if (!_.isEmpty(this._siteFeed)) {
            this._unregisterSiteFeedListeners();
            await this._siteFeed.shutdown();
        }
        if (!_.isEmpty(this._engineZoneFeed)) {
            this._unregisterEngineZoneFeedListeners();
            await this._engineZoneFeed.shutdown();
        }
        if (!_.isEmpty(this._orgZoneFeed)) {
            this._unregisterOrgZoneFeedListeners();
            await this._orgZoneFeed.shutdown();
        }
        if (!_.isEmpty(this._customFieldEntity)) {
            this._unregisterCustomFieldListeners();
            await this._customFieldEntity.shutdown();
        }
        if (!_.isEmpty(this._odmOsPlatformsFeed)) {
            this._unregisterOdmOsPlatformFeedListeners();
            await this._odmOsPlatformsFeed.shutdown();
        }
        if (!_.isEmpty(this._odmValueMapFeed)) {
            this._unregisterOdmValueMapFeedListeners();
            await this._odmValueMapFeed.shutdown();
        }
        if (!_.isEmpty(this._haPairFeed)) {
            await this._haPairFeed.shutdown();
        }

        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._device = undefined;
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
                this._osImageServerEntity = undefined;
                this._osImageServerFeed = undefined;
                this._siteEntity = undefined;
                this._siteFeed = undefined;
                this._engineZoneEntity = undefined;
                this._engineZoneFeed = undefined;
                this._orgZoneEntity = undefined;
                this._orgZoneFeed = undefined;
                this._haPairFeed = undefined;
                this._licenseRestriction = undefined;
                this._isOSMSolutionAvailable = undefined;
                this._customFieldEntity = undefined;
                this._connectionDropdownList = undefined;
                this._connectionTypeDefs = undefined;
                this._isDeviceControlled = undefined;
                this._restoreCommandsWithCredsName = undefined;
                this._extendedPropTypeEntityDef = undefined;
                this._extendedPropTypes = undefined;
                this._extendedPropsEntityDef = undefined;
                this._extendedPropFeed = undefined;
                this._deviceCategory = undefined;
                this._extendedReadOnlyProps = undefined;
                this._isEdpEntityAddedOrRemoved = undefined;
                this._odmOsPlatformEntityDef = undefined;
                this._odmOsPlatformsFeed = undefined;
                this._odmValueMapFeed = undefined;
                this._TagsEntity = undefined;
                this._tags = undefined;
                this._tagOptions = undefined;
            },
            (err) => {
                this._logger.warn(err, "DeviceDetailsViewComponent.shutdown() error.");
            }
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onDirtyStateChanged(/*dirtyState*/) {
        let isDirty = this.isDirty();

        this._gameEditor.setDirtyState(isDirty);
        this.emit("Dirty", isDirty);

        let onBeforeUnloadFunc = () => {
            return this._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    if (this.isDeviceFormValid(true)) {
                        return this.saveEdp().then(() => undefined); // it will not close the tab if it will have something in result
                    }
                    return Promise.resolve();
                })
                .then(() => {
                    if (this.isDeviceFormValid(true)) {
                        return this.saveDevice(false).then(() => undefined); // it will not close the tab if it will have something in result
                    }
                })
                .catch((err) => {
                    if (err.userRejected) {
                        return Promise.resolve(this._device.clearPendingPatches(true))
                            .then(() => this._modalUserFeedback.hideDisplayed())
                            .then(() => this._gameEditor.getTabCmpnt().setDirty(false))
                            .then(() => undefined);
                    } else {
                        console.error("Error in onBeforeUnloadFunc! Err:", err);
                    }
                });
        };

        if (isDirty) {
            this._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
        } else {
            this._gameEditor.clearOnBeforeUnload();
        }
    }

    _onPendingPatchesChanged(patches, oldPatches) {
        // Case 25550
        // ToDo: Could be called redundandtly which needs to be fixed.
        // Though shouldnt be called more than a couple of times for a single change in hapair due to conditions
        if (!patches) {
            return;
        }
        const haPatch = patches.filter((patch) => {
            if (patch.path && patch.path.search("haPairName") >= 0) {
                return true;
            }
            return false;
        });
        const oldHaPatch =
            oldPatches &&
            oldPatches.filter((patch) => {
                if (patch.path && patch.path.search("haPairName") >= 0) {
                    return true;
                }
                return false;
            });
        if (
            haPatch.length <= 0 ||
            (oldHaPatch && oldHaPatch.length > 0 && haPatch[0].value === oldHaPatch[0].value)
        ) {
            return;
        }
        this.emit("HAPairPatch", haPatch);
        // TODO - this is a quick hack because the Array ref adapter does a shutdown and complete rebuild every time you call setValue. the effect to the user is they lose focus in the proxy list if they stop typing longer than the debounce
        // this.emit('DevicePropertiesChanged');
    }

    _onPristinePropertiesChanged(pristineProperties, before) {
        let restoreCommands = _.get(pristineProperties, "restoreCommands", []);

        if (restoreCommands.length > 0) {
            this._CredentialsEntity
                .convertTemplateStringCredIdsToNames(this._userSession.activeOrganization.id, [
                    ...restoreCommands,
                ])
                .then((restoreCommandsWithCredsName) => {
                    this._restoreCommandsWithCredsName = restoreCommandsWithCredsName;
                })
                .catch((err) => {
                    this._logger.warn(
                        {error: err},
                        "DeviceDetailsViewComponent._onPristinePropertiesChanged() - Error running convertTemplateStringCredIdsToNames()"
                    );
                })
                .finally(() => {
                    this.emit("DevicePropertiesChanged", pristineProperties, before);
                });
        } else {
            this._restoreCommandsWithCredsName = [];
            this.emit("DevicePropertiesChanged", pristineProperties, before);
        }
    }

    _onEdpPristinePropertiesChanged(pristineProperties, before) {
        this.emit("ExtendedPropertiesChanged", pristineProperties, before);
    }

    // Event to check if edp types have changed
    _onEdpEntityRemoved() {
        this._isEdpEntityAddedOrRemoved = true;
        this.emit("ExtendedPropsTypesAddedOrRemoved");
    }
    _onEdpEntityAdded() {
        this._isEdpEntityAddedOrRemoved = true;
        this.emit("ExtendedPropsTypesAddedOrRemoved");
    }

    _onOsImageServerFeedUpdated(/*entity, patches, props, feed*/) {
        //console.log(entity, patches, props, feed);
        this.emit("OsImageServerFeedUpdated");
    }

    _onSiteFeedUpdated(/*entity, patches, props, feed*/) {
        //console.log(entity, patches, props, feed);
        // console.log("Feedupdate!");
        this.getSitesList().then(() => {
            // console.log("feedupdate");
            this.emit("SiteFeedUpdated");
        });
    }

    _onInstanceRemovedFromFeed(instance, cause) {
        // console.log(instance, cause);
        if (cause === "Deleted") {
            this._onSiteFeedUpdated(instance);
        }
    }

    _onEngineZoneFeedUpdated(/*entity, patches, props, feed*/) {
        //console.log(entity, patches, props, feed);
        this.emit("EngineZoneFeedUpdate");
    }

    _onOrgZoneFeedUpdated(/*entity, patches, props, feed*/) {
        //console.log(entity, patches, props, feed);
        this.emit("OrgZoneFeedUpdate");
    }

    _onCustomFieldsUpdated() {
        this.loadCustomFieldColumnDefinitions().then(() => {
            this.emit("CustomFieldUpdated");
        });
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _deepFindKeyValuePair(obj, searchKey, searchValue) {
        if (typeof obj !== "object" || obj === null) {
            return false;
        }

        for (const [key, value] of Object.entries(obj)) {
            if (key === searchKey && value.startsWith(searchValue)) {
                return true;
            }

            if (typeof value === "object") {
                if (this._deepFindKeyValuePair(value, searchKey, searchValue)) {
                    return true;
                }
            }
        }

        return false;
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    isDirty() {
        let isDirty = this._device.isDirty();
        _.values(this._extendedPropFeed.getCache()).forEach((extendedProp) => {
            isDirty = isDirty || extendedProp.isDirty();
        });
        return isDirty;
    }

    get gameEditor() {
        return this._gameEditor;
    }

    get logger() {
        return this._logger;
    }

    get osImageServerList() {
        if (!_.isEmpty(this._osImageServerFeed)) {
            return this._osImageServerFeed.getCacheAsArray();
        }
        return [];
    }

    get siteList() {
        return this._sitesList;
    }

    /** @deprecated use osImageServerMainId */
    get osImageServerMasterId() {
        return this.osImageServerMainId;
    }

    get osImageServerMainId() {
        return _.get(
            this.osImageServerList.find((osImageServer) => osImageServer.isMain),
            "id"
        );
    }

    get osImageServerIds() {
        return this.osImageServerList.map((osImageServer) => osImageServer.id);
    }

    get osImageServerNames() {
        return this.osImageServerList.map((osImageServer) => osImageServer.name);
    }

    get siteIds() {
        const siteIds = this.siteList.map((site) => site.id);
        // siteIds.unshift("/");
        return siteIds;
    }

    get sitePaths() {
        const sitePaths = this.siteList.map((site) => {
            let path = site.path || "/";
            path = path.replace("/" + this._userSession.activeOrganization.name, "");
            return path;
        });
        // sitePaths.unshift("/");
        return sitePaths;
    }

    getDeviceRelatedImageServerId() {
        return this.getOsImageServerId(this._device.fileServerIdentifier);
    }

    getOsImageServerId(identifier) {
        if (!identifier) {
            return;
        }
        return _.get(
            this.osImageServerList.find((osImageServer) => osImageServer.identifier === identifier),
            "id"
        );
    }

    getDeviceRelatedImageServerName() {
        return this.getOsImageServerName(this._device.fileServerIdentifier);
    }

    getOsImageServerName(identifier) {
        if (!identifier) {
            return;
        }
        return _.get(
            this.osImageServerList.find((osImageServer) => osImageServer.identifier === identifier),
            "name"
        );
    }

    // engine zone access
    get orgZoneList() {
        return this._orgZoneFeed.getCacheAsArray();
    }

    getOrgZoneByEngineZoneId(engineZoneId) {
        let selectedOrgZone;
        this.orgZoneList.forEach((orgZone) => {
            if (orgZone.engineZoneId === engineZoneId) {
                selectedOrgZone = orgZone;
            }
        });
        return selectedOrgZone;
    }

    /**
     * Get the engines that should be available in the drop down list.
     */
    get visibleEngineZoneList() {
        // set the default foundSelectedEngine to based on if we have an
        // engine zone for this device.  If no engine zone is set then
        // we don't have to find one in the engines define.
        let foundSelectedEngine = _.isEmpty(this._device.getEngineZoneId());
        let filteredEngines = _.filter(this._engineZoneFeed.getCacheAsArray(), (engineZone) => {
            // If the orgZone is equal to the current device engineZone or
            // the orgZone is enabled. Add this to the visible engines.
            // We should always include the current device engineZone in the list.
            let orgZone = this.getOrgZoneByEngineZoneId(engineZone.id);
            if (this._device.getEngineZoneId() === engineZone.id) {
                foundSelectedEngine = true;
            }
            return (
                this._device.getEngineZoneId() === engineZone.id || (orgZone && orgZone.zoneEnabled)
            );
        });
        if (this._device.getEngineZoneId() && !foundSelectedEngine) {
            let bogusEngine = {
                id: this._device.getEngineZoneId(),
                name: "Bogus Engine",
                currentState: "DELETED",
            };
            filteredEngines.push(bogusEngine);
        }
        return filteredEngines;
    }

    get engineZoneIds() {
        return this.visibleEngineZoneList.map((orgZone) => orgZone.id);
    }

    get engineZones() {
        return this._engineZoneFeed.cache;
    }

    get zoneNames() {
        return this.visibleEngineZoneList.map((engineZone) => {
            let orgZone = this.getOrgZoneByEngineZoneId(engineZone.id);
            let displayName;
            if (orgZone) {
                displayName = engineZone.displayName;
                if (engineZone.currentState !== "ACTIVE") {
                    displayName += " (Offline)";
                }
                if (!orgZone.zoneEnabled) {
                    displayName += " (Disabled in system settings) - Please select another zone";
                }

                if (engineZone.currentState === "DELETED") {
                    displayName = "Deleted Engine";
                }
            } else {
                displayName = "Unknown Engine";
            }
            return displayName;
        });
    }

    loadCustomFieldColumnDefinitions() {
        return this._CustomFieldsEntityDef
            .loadColumnDefinitions(this._userSession.activeOrganization.id)
            .then((customFieldColumnDef) => {
                this._customFieldColumnDef = customFieldColumnDef;
                return Promise.resolve();
            });
    }

    whenModelIsReady() {
        return this._gameEditor
            .whenDeviceIsReady()
            .then(() => {
                this._device = this._gameEditor.getDevice();

                this._deviceCategory = this._device.getDeviceRawCategory();
                const sId = this._userSession.activeOrganization.id;
                // The siteId has no setter in the device entity.
                // It is set by using the setSiteRelationship as done in the renderer.
                // let sId = this._device.siteId;
                // if (!this._device.siteId) {
                //     sId = this._userSession.activeOrganization.id;
                //     this._device.setSiteId(this._userSession.activeOrganization.id);
                //     return this._device
                //         .update({skipUpdatedOn: true, skipUpdatedBy: true, skipAudit: true})
                //         .catch(() => {
                //             return Promise.resolve(sId);
                //         })
                //         .then(() => {
                //             return Promise.resolve(sId);
                //         });
                // }
                return Promise.resolve(sId);
            })
            .then((sId) => {
                return this._siteEntity.loadSite(this._userSession.activeOrganization.id, sId);
            })
            .then((site) => {
                this._parentSite = site;
                return this._connectionInfoEntityDef.buildJtypeSelectOptions(
                    this._userSession.activeOrganization.id
                );
            })
            .then((jtypeOptionsAndTypeDefs) => {
                let jtypeSelectOptions = jtypeOptionsAndTypeDefs.selectOptions;
                // Fallback to cli default connection
                if (_.isEmpty(_.get(jtypeSelectOptions, "connectionType@Select.options"))) {
                    this._connectionDropdownList = {
                        "connectionType@Select.options": {
                            cliConnection: this._DeviceEntity.connectionInformationJtype,
                            "cliConnection@Display.label": " CLI connection",
                        },
                    };
                } else {
                    this._connectionDropdownList = jtypeSelectOptions;
                    this._connectionTypeDefs = jtypeOptionsAndTypeDefs.typeDefs;
                }
                return this._CustomFieldsEntityDef.retrieveCustomFields(
                    this._userSession.activeOrganization.id
                );
            })
            .then((customFieldEntity) => {
                this._customFieldEntity = customFieldEntity;
                this._registerCustomFieldListeners();
                return Promise.resolve();
            })
            .then(() => this._getExtendedPropTypes())
            .then(() => {
                return this.loadCustomFieldColumnDefinitions();
            })
            .then(() => {
                // Temporary workaround to prevent editor to go dirty due to missing custom field values
                return this._customFieldSanitizer();
            })
            .then(() => {
                if (
                    this._deviceCategory !== "iot" &&
                    this._device.getBaseControlType() === "CONTROLLED"
                ) {
                    // _isDeviceControlled doesn't include hybrid controlled devices
                    this._isDeviceControlled = true;
                }

                if (this._isOSMSolutionAvailable === true) {
                    return this._osImageServerEntity
                        .startAscendOsImageServersFeed(
                            this._userSession.activeOrganization.id,
                            {},
                            {
                                id: true,
                                name: true,
                                identifier: true,
                                isMain: true,
                            }
                        )
                        .whenInitialized();
                } else {
                    return Promise.resolve({});
                }
            })
            .then((osImageServerFeed) => {
                this._osImageServerFeed = osImageServerFeed;
                if (this._isOSMSolutionAvailable === true) {
                    this._registerServerFeedListeners();
                }
                return this._engineZoneEntity
                    .startAscendEngineZonesFeed(
                        this._userSession.activeOrganization.id,
                        {},
                        {
                            id: true,
                            currentState: true,
                            displayName: true,
                        }
                    )
                    .whenInitialized();
            })
            .then((engineZoneFeed) => {
                this._engineZoneFeed = engineZoneFeed;
                this._registerEngineZoneListeners();
                return this._siteEntity
                    .startSitesFeed(
                        this._userSession.activeOrganization.id,
                        {},
                        {
                            id: true,
                            path: true,
                        },
                        {
                            path: 1,
                        }
                    )
                    .whenInitialized();
            })
            .then((siteFeed) => {
                this._siteFeed = siteFeed;
                this._registerSiteFeedListeners();
                return this.getSitesList();
            })
            .then(() => {
                return this._orgZoneEntity
                    .startAscendOrgZonesFeed(this._userSession.activeOrganization.id, {}, {})
                    .whenInitialized();
            })
            .then((orgZoneFeed) => {
                this._orgZoneFeed = orgZoneFeed;
                this._registerOrgZoneListeners();
                return this._device.startRelatedHaPairFeed({});
            })
            .then((hapair) => {
                this._haPairFeed = hapair;
                let restoreCommands = _.get(
                    this._device._pristineProperties,
                    "restoreCommands",
                    []
                );
                return this._CredentialsEntity.convertTemplateStringCredIdsToNames(
                    this._userSession.activeOrganization.id,
                    [...restoreCommands]
                );
            })
            .then((restoreCommandsWithCredsName) => {
                this._restoreCommandsWithCredsName = restoreCommandsWithCredsName;
                this._registerDeviceListeners();
                if (this._deviceCategory === "iot") {
                    return this._startOdmOsPlatformsFeed().then(() => {
                        return this._startOdmValueMapFeed();
                    });
                }
                return Promise.resolve();
            })
            .then(() => {
                return this._TagsEntity.loadTags(this._userSession.activeOrganization.id);
            })
            .then((tags) => {
                this._tags = tags;
                this._refreshTagOptions();
            })
            .catch((err) => {
                this._logger.warn(err, "DeviceDetailsViewComponent.whenModelIsReady() error");
                this._modalUserFeedback.displayError({
                    errorMsg: "This device cannot be opened.",
                    errorCode: "",
                });
                return Promise.reject(err);
            });
    }

    _customFieldSanitizer() {
        const activeCustomFieldIds = Object.keys(this._customFieldColumnDef || {}).filter(
            (uuid) => this._customFieldColumnDef[uuid].systemOwned !== true
        );

        let customFieldValues = this._device.customFieldValues
            ? _.cloneDeep(this._device.customFieldValues)
            : {};

        activeCustomFieldIds.forEach((uuid) => {
            if (typeof customFieldValues[uuid] === "undefined") {
                if (this._device.customFieldSources[uuid]) {
                    if (_.isNil(_.get(this._parentSite, ["customFieldValues", uuid]))) {
                        customFieldValues[uuid] = null;
                    } else {
                        customFieldValues[uuid] = this._parentSite.customFieldValues[uuid];
                    }
                } else {
                    customFieldValues[uuid] = _.get(
                        this._customFieldColumnDef,
                        [uuid, "default"],
                        null
                    );
                }
            }
        });
        this._device.setCustomFieldValues(customFieldValues);

        if (_.isEmpty(this._device.pendingPatches)) {
            return Promise.resolve();
        } else {
            return this._device
                .update({skipUpdatedOn: true, skipUpdatedBy: true, skipAudit: true})
                .catch((err) => {
                    this._logger.error(
                        {error: err},
                        err,
                        "DeviceDetailsViewRenderer: _customFieldSanitizer - Update Error"
                    );
                })
                .finally(() => {
                    return Promise.resolve();
                });
        }
    }

    resetDeviceProperties() {
        _.values(this._extendedPropFeed.getCache()).forEach((extendedProp) => {
            extendedProp.clearPendingPatches();
            extendedProp.clearPendingRelationships();
        });
        this._device.clearPendingPatches();
        this._device.clearPendingRelationships(); // Case 25550 - clearing relationships as well on cancel and save
        return Promise.resolve();
    }

    generatePlatformOptionsMap() {
        const platforms = this._odmOsPlatformsFeed.getCache();
        const opts = [];
        for (const [key, val] of Object.entries(platforms)) {
            opts.push({
                [key]: val.name,
            });
        }
        return opts.sort((a, b) => {
            const nameA = Object.values(a)[0];
            const nameB = Object.values(b)[0];
            return nameA.localeCompare(nameB);
        });
    }

    get _odmOsPlatformsMap() {
        return this.generatePlatformOptionsMap();
    }

    generateTagOptionsMap() {
        const labelIdMap = {};
        const options = (this._tags || []).map((tag) => {
            labelIdMap[tag.name] = tag.id;
            return {
                [tag.id]: tag.name,
            };
        });
        options.sort((a, b) => {
            const nameA = Object.values(a)[0];
            const nameB = Object.values(b)[0];
            return nameA.localeCompare(nameB);
        });
        return {options, labelIdMap};
    }

    _refreshTagOptions() {
        const {options, labelIdMap} = this.generateTagOptionsMap();
        this._tagOptions = options;
        this._tagOptionsLabelIdMap = labelIdMap;
    }

    get tagOptions() {
        return this._tagOptions || [];
    }

    generateSiteOptionsMap(sitesList) {
        return sitesList.reduce((acc, site) => {
            let path = site.path || "/";
            path = path.replace("/" + this._userSession.activeOrganization.name, "");
            acc[path] = {
                path,
                id: site.id,
                type: site.locationType,
                code: site.codeName,
            };
            return acc;
        }, {});
    }

    generateSiteNameMap(sitesList) {
        return sitesList.reduce((acc, site) => {
            acc[site.id] = site.name;
            return acc;
        }, {});
    }

    loadSites() {
        return this._siteEntity.loadSites(
            this._userSession.activeOrganization.id,
            {},
            {
                id: true,
                path: true,
                codeName: true,
                locationType: true,
                name: true,
            },
            {
                path: 1,
            }
        );
    }

    getSitesList() {
        return this.loadSites().then((sites) => {
            this._sitesList = sites;
            this._siteOptionsMap = this.generateSiteOptionsMap(this._sitesList);
            this._siteNamemap = this.generateSiteNameMap(this._sitesList);
            return Promise.resolve();
        });
    }

    _startOdmOsPlatformsFeed() {
        return this._odmOsPlatformEntityDef
            .startOdmOsPlatformsFeed(
                this._userSession.activeOrganization.id,
                {},
                {
                    id: true,
                    name: true,
                    manufacturer: true,
                    model: true,
                    deviceType: true,
                    osName: true,
                    osFamily: true,
                    osReleaseName: true,
                    osVersion: true,
                    machineArchitecture: true,
                    language: true,
                    locale: true,
                },
                {},
                {
                    skip: 0,
                    limit: PLATFORM_FEED_LIMIT,
                }
            )
            .whenInitialized()
            .then((feed) => {
                this._odmOsPlatformsFeed = feed;
                this._registerOdmOsPlatformFeedListeners();
                return Promise.resolve();
            });
    }

    getOdmOsPlatformById(platformId) {
        const platforms = this._odmOsPlatformsFeed.getCache();
        return platforms[platformId];
    }

    getOdmOsPlatformsList() {
        return _.values(this._odmOsPlatformsFeed.getCache());
    }

    _startOdmValueMapFeed() {
        return this._ValueMap
            .startValueMapsFeed(
                this._userSession.activeOrganization.id,
                {name: "xdome"},
                {id: true, name: true, valueMap: true}
            )
            .whenInitialized()
            .then((feed) => {
                this._odmValueMapFeed = feed;
                this._registerOdmValueMapFeedListeners();
                this._setOdmFieldOptions();
                return Promise.resolve();
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceDetailsViewComponent._startOdmValueMapFeed() error - using empty options"
                );
                this._deviceTypeOptions = [];
                this._manufacturerOptions = [];
                this._modelOptions = [];
                return Promise.resolve();
            });
    }

    _setOdmFieldOptions() {
        try {
            const cache = _.values(this._odmValueMapFeed.getCache());
            const xdomeEntity = cache[0];
            const fieldMappings = xdomeEntity ? xdomeEntity.valueMap : {};
            this._deviceTypeOptions = this._generateOptionsForField(
                fieldMappings ? fieldMappings.deviceType : null
            );
            this._manufacturerOptions = this._generateOptionsForField(
                fieldMappings ? fieldMappings.manufacturer : null
            );
            this._modelOptions = this._generateOptionsForField(
                fieldMappings ? fieldMappings.model : null
            );
        } catch (err) {
            this._logger.warn(
                {error: err},
                "DeviceDetailsViewComponent._setOdmFieldOptions() error - using empty options"
            );
            this._deviceTypeOptions = [];
            this._manufacturerOptions = [];
            this._modelOptions = [];
        }
    }

    _generateOptionsForField(fieldMapObj) {
        if (!fieldMapObj) {
            return [];
        }
        return Object.keys(fieldMapObj)
            .filter((key) => fieldMapObj[key])
            .map((key) => ({
                [key]: key,
            }));
    }

    isDeviceFormValid(rollbackInvalidData = false) {
        const device = this._device;
        const inputChanges = device.getProperties();
        const oldData = device.getPristineProperties();
        const isInputNameEmpty = inputChanges.name.trim().length === 0;

        // general form validation
        if (this._renderer._formsComponent.isFormInvalid()) {
            throw {
                errorMsg:
                    "Invalid or missing form data present. Please validate that all required entries are correct.",
                errorCode: "InvalidForm",
                title: "Error",
            };
        }

        // specific state form validation and sanitizing
        if (this._deviceCategory !== "iot" && inputChanges.connectionInformation.ip) {
            let connectionInformation = inputChanges.connectionInformation;
            connectionInformation.ip = connectionInformation.ip.trim();
            device.setConnectionInformation(connectionInformation);
        }

        if (isInputNameEmpty && rollbackInvalidData === false) {
            throw {
                errorMsg: "Name cannot be left empty. Please enter a valid device name.",
                errorCode: "SyntaxError",
                title: "Error",
            };
        } else if (isInputNameEmpty && rollbackInvalidData === true) {
            device.setName(oldData.name.trim());
            return true;
        } else {
            device.setName(inputChanges.name.trim());
            return true;
        }
    }

    async saveEdp() {
        try {
            this.isDeviceFormValid();
            _.values(this._extendedPropFeed.getCache()).forEach(async (extendedProp) => {
                if (extendedProp.isDirty()) {
                    await extendedProp.update();
                }
            });
        } catch (err) {
            this._logger.warn({error: err}, "DeviceDetailsViewComponent.saveEdp() error.");
            if (err.errorCode === "NoPendingUpdates") {
                this.emit("DevicePropertiesChanged");
                return Promise.reject();
            } else {
                return this._modalUserFeedback.hideDisplayed().then(() => {
                    return this._modalUserFeedback.displayError(err);
                });
            }
        }
    }

    // Runs before update() so a tag-creation failure rejects the whole save.
    _resolveNewTagIds() {
        const tagIdsPatched = (this._device.pendingPatches || []).some(
            (patch) => patch.path.search("/tagIds") >= 0
        );
        if (!tagIdsPatched) {
            return Promise.resolve();
        }
        const tagIds = this._device.getTagIds();
        if (!tagIds.length) {
            return Promise.resolve();
        }
        const newTags = [],
            existingTags = [];
        tagIds.forEach((tag) => {
            if (tag.search(/^__new__/) >= 0) {
                newTags.push(tag);
            } else {
                existingTags.push(tag);
            }
        });
        if (!newTags.length) {
            return Promise.resolve();
        }
        const newTagNames = newTags.map((tag) => tag.replace(/^__new__/, ""));
        this._validateNewTagNames(newTagNames);
        return Promise.all(
            newTagNames.map((name) => {
                const tagEntity = new this._TagsEntity(this._userSession.activeOrganization.id);
                tagEntity.name = name;
                return tagEntity.create();
            })
        ).then((createdTags) => {
            this._tags = this._tags.concat(createdTags);
            this._refreshTagOptions();
            this._device.setTagIds(existingTags.concat(createdTags.map((tag) => tag.id)));
            this.emit("TagOptionsUpdated");
        });
    }

    /**
     * Applies the same name-length/charset rules as Settings > Custom Fields > Tags
     * to tags created on the fly, before they're sent to the server.
     * @param {string[]} names
     * @throws {{errorMsg: string, errorCode: string, title: string}}
     */
    _validateNewTagNames(names) {
        const invalidLength = names.find((name) => name.length > MAX_TAG_NAME_LENGTH);
        if (invalidLength) {
            throw {
                errorMsg: TAG_NAME_LENGTH_ERROR_MSG(invalidLength),
                errorCode: "SyntaxError",
                title: "Error",
            };
        }
        const invalidCharset = names.find((name) => !TAG_NAME_CHARSET_PATTERN.test(name));
        if (invalidCharset) {
            throw {
                errorMsg: TAG_NAME_CHARSET_ERROR_MSG(invalidCharset),
                errorCode: "SyntaxError",
                title: "Error",
            };
        }
    }

    saveDevice() {
        return Promise.resolve()
            .then(() => this._resolveNewTagIds())
            .then(() => {
                //do nothing unless this guy is dirty
                if (this._device.isDirty()) {
                    this.isDeviceFormValid();
                    if (
                        this._isTopologyAvailable &&
                        (this._device.pristineProperties.discoveryLevel !==
                            this._device.properties.discoveryLevel ||
                            this._device.pristineProperties.discoveryLevelFrom !==
                                this._device.properties.discoveryLevelFrom) &&
                        this._device.properties.discoveryLevel < 3
                    ) {
                        return this._modalUserFeedback
                            .displayConfirmation(
                                "You have a Topology license for this organization. Set the Discovery Level to 3 for best results. Levels 1 and 2 do not provide sufficient information for Topology.",
                                {
                                    notDismissable: true,
                                    title: "Discovery Level",
                                    confirmDetails: {
                                        text: "OK",
                                        type: "additive",
                                    },
                                    rejectDetails: {
                                        text: "Cancel",
                                        type: "text",
                                    },
                                }
                            )
                            .then(() => {
                                return this._device.update();
                            });
                    }

                    if (
                        (this._device.properties.siteId !== this._parentSite.id &&
                            !this._parentSite.credsName &&
                            this._deepFindKeyValuePair(
                                this._device.properties.connectionInformation,
                                "instanceName",
                                "_Site"
                            )) ||
                        (this._device.pendingPatches &&
                            this._device.pendingPatches.some(
                                (patch) =>
                                    (patch.op === "add" || patch.op === "replace") &&
                                    ((patch.path.startsWith("/connectionInformation") &&
                                        this._deepFindKeyValuePair(
                                            patch.value,
                                            "instanceName",
                                            "_Site"
                                        )) ||
                                        (patch.path.match(
                                            /^\/connectionInformation.*credentials.*instanceName$/
                                        ) &&
                                            patch.value === "_Site"))
                            ) &&
                            !this._parentSite.credsName)
                    ) {
                        return this._modalUserFeedback
                            .displayConfirmation(
                                "This device inherits its credentials from the parent site, but the parent currently has no assigned credentials. Saving your changes leaves this device without credentials. Do you want to proceed to save?",
                                {
                                    notDismissable: true,
                                    title: "No Credentials",
                                    confirmDetails: {
                                        text: "OK",
                                        type: "additive",
                                    },
                                    rejectDetails: {
                                        text: "Cancel",
                                        type: "text",
                                    },
                                }
                            )
                            .then(() => {
                                return this._device.update();
                            });
                    }
                    //console.log(this._device.connectionInformation.credentials["@ref"].instance);
                    return this._device.update();
                }
            })
            .catch((err) => {
                this._logger.warn({error: err}, "DeviceDetailsViewComponent.saveDevice() error.");
                if (err.userRejected) {
                    return Promise.reject();
                }
                if (err.errorCode === "NoPendingUpdates") {
                    this.emit("DevicePropertiesChanged");
                    return Promise.reject();
                } else {
                    return this._modalUserFeedback.hideDisplayed().then(() => {
                        return this._modalUserFeedback.displayError(err);
                    });
                }
            });
    }

    updateRelationships() {
        return Promise.resolve()
            .then(() => {
                //do nothing unless this guy is dirty
                if (this._device.isDirty()) {
                    return this._device.updateRelationships();
                }
            })
            .then(() => {
                if (!this.isDirty()) {
                    this._gameEditor.clearOnBeforeUnload();
                }
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "DeviceDetailsViewComponent.updateRelationships() error."
                );
                if (err.errorCode === "NoPendingUpdates") {
                    return Promise.reject();
                } else {
                    return this._modalUserFeedback.displayError(err);
                }
            });
    }

    _findReadOnlyPropertiesDeep(obj) {
        const readOnlyProps = [];
        const recurse = (node) => {
            if (!_.isPlainObject(node)) {
                return;
            }
            for (const [key, value] of Object.entries(node)) {
                if (key.endsWith("@Display.readOnly") && value === true) {
                    const propName = key.replace("@Display.readOnly", "");
                    readOnlyProps.push(propName);
                }
                if (_.isPlainObject(value)) {
                    recurse(value);
                }
            }
        };
        recurse(obj);
        return readOnlyProps;
    }

    async _getExtendedPropTypes() {
        const initialEdpData = await this._device.loadRelatedEdpData({enabled: true});
        const extendedPropTypeList = initialEdpData
            .filter((edp) => edp.getEnabled())
            .map((edp) => edp.getExtendedPropsTypeId());
        // Retrieving ExtendedPropTypes which are enabled and not disabled for device details
        this._extendedPropTypes = await this._extendedPropTypeEntityDef.loadExtendedPropTypes(
            this._userSession.activeOrganization.id,
            {
                $and: [
                    {id: {$in: extendedPropTypeList}},
                    {enabled: {$ne: false}},
                    {deviceDetailsDisabled: {$ne: true}},
                    {"metadata.deviceDetailsDisabled": {$ne: true}},
                ],
            }
        );
        const enabledExtendedPropTypeList = this._extendedPropTypes.map((t) => t.getId());
        // Second feed: kept alive for listeners registered below
        this._extendedPropFeed = await this._device.startRelatedEdpDataFeed({
            enabled: true,
            extendedPropsTypeId: {$in: enabledExtendedPropTypeList},
        });
        await this._extendedPropFeed.whenInitialized();
        this._extendedReadOnlyProps = this._extendedPropTypes.flatMap((t) =>
            this._findReadOnlyPropertiesDeep(t.getJtype())
        );
        this._registerExtendedPropsListeners();
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

DeviceDetailsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.entities.CustomFields",
    "$teve.entities.OsImageServer",
    "$teve.entities.EngineZone",
    "$teve.entities.OrgZone",
    "$teve.licenseRestriction",
    "$teve.ui.userFeedback",
    "$teve.solutionsManager",
    "$teve.storageFactory",
    "$teve.entities.Device",
    "$teve.entities.Site",
    "$teve.entities.ConnectionInfo",
    "$teve.jnibAdapter",
    "$teve.entities.Credentials",
    "$teve.entities.ExtendedPropType",
    "$teve.entities.ExtendedProps",
    "$teve.entities.OdmOsPlatform",
    "$teve.entities.ValueMap",
    "$teve.entities.Tags",
];

module.exports = DeviceDetailsViewComponent;

DeviceDetailsViewComponent.$displayDetails = {
    type: "View",
    name: "Device Details",
    icon: "configdrift.device_details",
    shown: true,
    title: "Details",
    order: 1,
};
